import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import testable from '@xingternal/malt-util/testable';
import { withFlavour } from '@xingternal/flavour';
import { Headline as HeadlineXdl } from '@xingternal/typography';
import { QA_MALT } from '@xingternal/malt-constants';

import styles from './index.css';

const sizes = ['xxlarge', 'xlarge', 'large', 'medium', 'small'];
const sizeOnConfined = [
  'xxlargeOnConfined',
  'xlargeOnConfined',
  'largeOnConfined',
  'mediumOnConfined',
  'smallOnConfined',
];
const sizeOnWide = [
  'xxlargeOnWide',
  'xlargeOnWide',
  'largeOnWide',
  'mediumOnWide',
  'smallOnWide',
];

const HeadlineOdl = ({
  level = 2,
  levelConfined,
  levelWide,
  className,
  children,
  noMargin,
  testId,
}) => {
  const TestableElement = testable(QA_MALT, `h${level}`);
  return (
    <TestableElement
      className={classNames(
        styles[`headline${level}`],
        className,
        styles.headline,
        {
          [styles.noMargin]: noMargin,
          [styles[`headlineConfined${levelConfined}`]]: levelConfined,
          [styles[`headlineWide${levelWide}`]]: levelWide,
        }
      )}
      testId={testId}
    >
      {children}
    </TestableElement>
  );
};

const Headline = ({
  flavour,
  className,
  level,
  levelConfined,
  levelWide,
  ...props
}) => {
  if (flavour === 'xdl') {
    const headlineProps = {
      ...props,
      [sizes[props.level - 1] || 'xlarge']: true,
      [sizeOnConfined[props.levelConfined - 1]]: true,
      [sizeOnWide[props.levelWide - 1]]: true,
      skin: { text: className },
    };
    return <HeadlineXdl {...headlineProps} />;
  } else {
    return (
      <HeadlineOdl
        level={level}
        levelConfined={levelConfined}
        levelWide={levelWide}
        className={className}
        {...props}
      />
    );
  }
};

Headline.displayName = 'Headline';

HeadlineOdl.propTypes = {
  /** define the level of the Headline */
  level: PropTypes.oneOf([1, 2, 3, 4, 5, 6]),
  /** define the level of the Headline in tablet sizes */
  levelConfined: PropTypes.oneOf([1, 2, 3, 4, 5, 6]),
  /** define the level for Headline in large screens   */
  levelWide: PropTypes.oneOf([1, 2, 3, 4, 5, 6]),
  /** add a custom className to the component */
  className: PropTypes.string,
  /** set the elements margin to 0 */
  noMargin: PropTypes.bool,
  /** insert the headline text */
  children: PropTypes.node.isRequired,
  /** define a testid for the paragraph */
  testId: PropTypes.string,
};

Headline.propTypes = { ...HeadlineOdl.propTypes, flavour: PropTypes.string };

export default withFlavour(Headline);
